import React, { useState, useMemo, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import SurgeonsTable from './SurgeonsTable';
import SurgeonsTableMobile from './SurgeonsTableMobile';
import FilterSurgeonsModal from './FilterSurgeonsModal';
import SurgeonModal from './SurgeonModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchSurgeons,
  updateSurgeon,
  deleteSurgeon,
  createSurgeon,
} from '../../actions/surgeonsActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './surgeons.scss';

const DUPLICATE_SURGEON_ERROR = 'A surgeon with this user already exists for the selected distributor.';

const SurgeonsTableContainer = (props) => {
  const {
    surgeons = [],
    distributors,
    createSurgeon,
    updateSurgeon,
    deleteSurgeon,
    fetchSurgeons,
    withButton,
    toggleSidebar,
    countries,
    hospitals = [],
    users = [],
    companies = [],
    currentUser,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedSurgeon, setSelectedSurgeon] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ countries: [], distributors: [] });

  const onEditClick = (surgeon) => {
    setErrorMessage('');
    setSelectedSurgeon(surgeon);
    toggleUpdateModal(true);
  };

  const hasDuplicateSurgeonAssignment = useCallback((formObj, currentId = null) => (
    surgeons.some((surgeon) => (
      surgeon.userId === formObj.userId
      && surgeon.distributor === formObj.distributor
      && surgeon.id !== currentId
    ))
  ), [surgeons]);

  const onSurgeonCreate = async (formObj) => {
    try {
      setErrorMessage('');

      if (hasDuplicateSurgeonAssignment(formObj)) {
        throw new Error(DUPLICATE_SURGEON_ERROR);
      }

      setLoading(true);

      await createSurgeon(formObj);
      await fetchSurgeons();
      setSuccessMessage('New surgeon successfully created');
      toggleCreateModal(false);
    } catch (err) {
      setErrorMessage(err?.message || 'Unable to create surgeon.');
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const onSurgeonUpdate = async (formObj) => {
    if (selectedSurgeon) {
      try {
        setErrorMessage('');

        if (hasDuplicateSurgeonAssignment(formObj, selectedSurgeon.id)) {
          throw new Error(DUPLICATE_SURGEON_ERROR);
        }

        setLoading(true);

        await updateSurgeon(selectedSurgeon?.id, formObj);
        await fetchSurgeons();
        setSuccessMessage('Surgeon successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        setErrorMessage(err?.message || 'Unable to update surgeon.');
        console.error(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteSurgeon = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteSurgeon(selectedId);
        await fetchSurgeons();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      companies: filters.companies || [],
      distributors: filters.distributors || [],
      roles: filters.roles || [],
      countries: filters.countries || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredSurgeons = useMemo(() => {
    let filtered = surgeons;

    if (search) {
      const q = search.toLowerCase();
      filtered = surgeons.filter((s) => {
        const user = users.find((u) => u.uid === s.userId);
        return user?.firstName?.toLowerCase()?.includes(q) || user?.lastName?.toLowerCase()?.includes(q)
      });
    }

    if (filterState?.companies?.length) {
      filtered = filtered?.filter((s) => filterState?.companies?.map((c) => c.id)?.includes(s.company));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((s) => {
        const distributor = distributors.find((u) => u.uid === s.distributor);
        return filterState?.countries?.includes(distributor?.country);
      });
    }

    if (filterState?.distributors?.length) {
      filtered = filtered?.filter((s) => filterState?.distributors?.map((d) => d.id)?.includes(s.distributor));
    }

    return filtered;
  }, [surgeons, search, filterState, users, distributors]);

  const list = useMemo(() => {
    let result = filteredSurgeons;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredSurgeons, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  const formatValue = useCallback((key, value) => {
    if (key === 'distributors' || key === 'companies') {
      return value?.name || '';
    }
    return value;
  }, []);

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Surgeons
                  <Box className="badge">{surgeons.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add or manage surgeons here
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add Surgeon'
              onClick={() => {
                setErrorMessage('');
                toggleCreateModal(true);
              }}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {surgeons.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{formatValue(key, value)}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }
              <SurgeonsTable
                surgeons={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredSurgeons?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                users={users}
                hospitals={hospitals}
                companies={companies}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <SurgeonsTableMobile surgeons={list} hospitals={hospitals} users={users} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Surgeons</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteSurgeon}
        title="Delete Surgeon"
        text="Are you sure you want to delete this surgeon?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <SurgeonModal
        open={showCreateModal}
        onClose={() => {
          setErrorMessage('');
          toggleCreateModal(false);
        }}
        onSubmit={onSurgeonCreate}
        countries={countries}
        loading={loading}
        users={users}
        hospitalsList={hospitals}
        distributors={distributors}
        companies={companies}
        currentUser={currentUser}
      />

      <SurgeonModal
        open={showUpdateModal}
        onClose={() => {
          setErrorMessage('');
          toggleUpdateModal(false);
          setSelectedSurgeon(undefined);
        }}
        onSubmit={onSurgeonUpdate}
        countries={countries}
        mode='update'
        initialValues={selectedSurgeon}
        loading={loading}
        users={users}
        hospitalsList={hospitals}
        distributors={distributors}
        companies={companies}
        currentUser={currentUser}
      />

      <FilterSurgeonsModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
        distributors={distributors}
        companies={companies}
        currentUser={currentUser}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteSurgeon}
        title="Delete Surgeon?"
        text="Are you sure you want to delete this surgeon? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />

      <Alert
        variant="error"
        message={errorMessage}
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
      />
    </>
  );
};

SurgeonsTableContainer.propTypes = {
  surgeons: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    surgeonTags: state.surgeons.tags,
    currentUser: state.user.currentUser,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchSurgeons: () => dispatch(fetchSurgeons()),
    createSurgeon: (surgeonData) => dispatch(createSurgeon(surgeonData)),
    updateSurgeon: (id, surgeonData) => dispatch(updateSurgeon(id, surgeonData)),
    deleteSurgeon: (id) => dispatch(deleteSurgeon(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SurgeonsTableContainer);
